import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ScrollArea } from '../components/ui/scroll-area';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Download, Filter, Clock, ChevronLeft, ChevronRight } from 'lucide-react';
import { format } from 'date-fns';

const ACTION_COLORS = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
};

const ENTITY_LABELS = {
  eod_record: 'EOD Record',
  sale_record: 'Sale Record',
  location: 'Location',
  user: 'User',
  dropdown: 'Dropdown',
  marketing_entry: 'Marketing Entry',
};

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ action: '', entity_type: '', date_from: '', date_to: '' });
  const limit = 50;

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (filters.action) params.action = filters.action;
      if (filters.entity_type) params.entity_type = filters.entity_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const res = await axios.get(`${API}/audit-logs`, { params });
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch { toast.error('Failed to load audit log'); }
    finally { setLoading(false); }
  }, [page, filters]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const handleExport = () => {
    const params = new URLSearchParams();
    if (filters.action) params.append('action', filters.action);
    if (filters.entity_type) params.append('entity_type', filters.entity_type);
    if (filters.date_from) params.append('date_from', filters.date_from);
    if (filters.date_to) params.append('date_to', filters.date_to);
    const token = localStorage.getItem('arch_token');
    const url = `${API}/audit-logs/export?${params}`;
    // Use fetch with auth header since window.open won't send auth
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'audit_log.csv';
        a.click();
      });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
            <Clock size={20} className="text-cyan-400" /> Audit Log
          </h1>
          <p className="text-slate-500 text-sm mt-1">{total.toLocaleString()} total events</p>
        </div>
        <Button onClick={handleExport} className="gap-2 bg-white text-slate-800 border border-slate-200 hover:bg-slate-50">
          <Download size={14} /> Export CSV
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Filter size={14} className="text-slate-400" />
            <Select value={filters.action || 'all'} onValueChange={v => { setPage(1); setFilters(f => ({ ...f, action: v === 'all' ? '' : v })); }}>
              <SelectTrigger className="h-8 text-xs w-32 bg-white"><SelectValue placeholder="All Actions" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="create">Create</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="delete">Delete</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.entity_type || 'all'} onValueChange={v => { setPage(1); setFilters(f => ({ ...f, entity_type: v === 'all' ? '' : v })); }}>
              <SelectTrigger className="h-8 text-xs w-36 bg-white"><SelectValue placeholder="All Types" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(ENTITY_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input type="date" value={filters.date_from} onChange={e => { setPage(1); setFilters(f => ({ ...f, date_from: e.target.value })); }}
              className="h-8 text-xs w-36 bg-white" />
            <Input type="date" value={filters.date_to} onChange={e => { setPage(1); setFilters(f => ({ ...f, date_to: e.target.value })); }}
              className="h-8 text-xs w-36 bg-white" />
            <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setFilters({ action: '', entity_type: '', date_from: '', date_to: '' }); setPage(1); }}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="bg-white shadow-sm">
        <CardContent className="p-0">
          <ScrollArea className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="border-slate-100">
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide w-40">Timestamp</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide w-32">User</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide w-20">Action</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide w-28">Type</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(5)].map((_, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}
                    </TableRow>
                  ))
                ) : logs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-slate-400">
                      <Clock className="mx-auto mb-2 text-slate-300" size={32} />
                      No audit events found
                    </TableCell>
                  </TableRow>
                ) : (
                  logs.map(log => (
                    <TableRow key={log.id} className="border-slate-50 hover:bg-slate-50">
                      <TableCell className="text-xs text-slate-500 whitespace-nowrap">
                        {log.timestamp ? format(new Date(log.timestamp), 'MMM d, yyyy HH:mm') : '—'}
                      </TableCell>
                      <TableCell className="text-xs font-medium text-slate-700">{log.username || '—'}</TableCell>
                      <TableCell>
                        <Badge className={`text-xs border-0 ${ACTION_COLORS[log.action] || 'bg-slate-100 text-slate-600'}`}>
                          {log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {ENTITY_LABELS[log.entity_type] || log.entity_type}
                      </TableCell>
                      <TableCell className="text-xs text-slate-600 max-w-md">
                        <div>{log.summary}</div>
                        {log.details?.changes && Object.keys(log.details.changes).length > 0 && (
                          <div className="mt-2 space-y-1">
                            {Object.entries(log.details.changes).map(([field, change]) => (
                              <div key={field} className="text-xs p-2 rounded bg-amber-50 border border-amber-200">
                                <span className="font-semibold text-amber-900">{field}:</span>
                                <span className="text-slate-600"> "{change.old}" → "{change.new}"</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {log.details?.import_method === 'excel' && (
                          <Badge className="mt-1 bg-purple-100 text-purple-800 text-xs">Excel Import</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </ScrollArea>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
              <span className="text-xs text-slate-500">Page {page} of {totalPages} ({total} events)</span>
              <div className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                  <ChevronLeft size={13} />
                </Button>
                <Button size="sm" variant="outline" className="h-7 w-7 p-0" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                  <ChevronRight size={13} />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
